import { useTranslation } from "react-i18next";

import envelope from "../../../../commons/libs/externals/envelope";
import { formatUnit } from "../../../../commons/libs/formatters";
import { CustomBicycleFilterKey } from "../../../../commons/specs/bicycle";
import { ActiveFilters } from "../../../../commons/specs/filters";
import { ProductType } from "../../../../commons/specs/product";
import { SizingBodyValues } from "../../../../commons/types/sizing";
import * as icons from "../../../../resources/icons";
import { ROUTES } from "../../../commons/routes";
import Button from "../../components/Button/Button";
import DescriptionListCard from "../../components/DescriptionListCard/DescriptionListCard";
import Headline from "../../components/Headline/Headline";
import Icon from "../../components/Icon/Icon";
import Note from "../../components/Note/Note";
import Paragraph from "../../components/Paragraph/Paragraph";
import { setActiveFilter } from "../../libs/filters";
import { useAppHistory } from "../../libs/hooks/use-app-routing";
import { useSettings } from "../../libs/queries/use-settings";
import { useCommonAppDispatcher, useCommonAppSelector } from "../../store/hooks";

interface Props {
  onClose: () => void;
  activeFilters: ActiveFilters<ProductType.Bicycle>;
  showSizingWarning: boolean;
  showSetFilterButton: boolean;
}

const BodySizingFilterModalPartial = ({ activeFilters, showSetFilterButton, showSizingWarning, onClose }: Props) => {
  const [dispatch, actions] = useCommonAppDispatcher();
  const history = useAppHistory();

  const settings = useSettings();

  const bodySizingBodyValues = useCommonAppSelector(state => state.session.bodyValues);

  const {
    t,
    i18n: { language: locale }
  } = useTranslation(["commons"]);

  const renderBodySizingNotification = () => {
    const button = envelope.isMobileBuild ? (
      <Button
        block
        variant="accent"
        kind="solid"
        onClick={() => history.push(ROUTES.DEVICE_INFORMATION_AND_SUPPORT.INDEX)}
        icon={<Icon source={icons.IconSmallArrowRight} />}
        iconRight
      >
        {t("commons:filterModalPartialProductFinder.sizing.notification.contactSupportButton")}
      </Button>
    ) : (
      <Button
        block
        variant="accent"
        kind="solid"
        onClick={() => history.push(ROUTES.SETTINGS.THIRD_PARTY_BODYSIZING)}
        icon={<Icon source={icons.IconSmallArrowRight} />}
        iconRight
      >
        {t("commons:filterModalPartialProductFinder.sizing.notification.activateBikeSizingButton")}
      </Button>
    );

    return (
      <>
        <Headline>{t("commons:filterModalPartialProductFinder.sizing.notification.headline")}</Headline>
        <Paragraph>{t("commons:filterModalPartialProductFinder.sizing.notification.description")}</Paragraph>
        {button}
      </>
    );
  };

  const renderWarning = () => {
    return (
      <Note spacing="s" kind="warning" icon={<Icon source={icons.IconSmallInputError} />}>
        <Paragraph size="s" kind="support">
          {t("commons:filterModalPartialProductFinder.sizing.limitWarning")}
        </Paragraph>
      </Note>
    );
  };

  const renderBodySizingValues = (bodySizingBodyValues: SizingBodyValues) => {
    return (
      <>
        <Headline>{t("commons:filterModalPartialProductFinder.sizing.resultsHeadline")}</Headline>
        <DescriptionListCard noCard classNames={["u-space-base"]}>
          <dt>
            {t("commons:variantsContentPartialProductDetails.filter.bodySizingFilterActivatedTooltip.bodyHeight")}:
          </dt>
          <dd>{formatUnit(bodySizingBodyValues.bodyHeight, locale, { unit: "centimeter" })}</dd>
          <dt>{t("commons:variantsContentPartialProductDetails.filter.bodySizingFilterActivatedTooltip.inseam")}:</dt>
          <dd>{formatUnit(bodySizingBodyValues.inseam, locale, { unit: "centimeter" })}</dd>
          <dt>{t("commons:variantsContentPartialProductDetails.filter.bodySizingFilterActivatedTooltip.armLength")}</dt>
          <dd>{formatUnit(bodySizingBodyValues.armLength, locale, { unit: "centimeter" })}</dd>
        </DescriptionListCard>
        {showSetFilterButton && (
          <Button
            block
            variant="accent"
            kind="solid"
            onClick={() => {
              onClose();
              dispatch(
                actions.session.setActiveFilters(
                  setActiveFilter<ProductType.Bicycle, CustomBicycleFilterKey.Sizing>(
                    activeFilters,
                    CustomBicycleFilterKey.Sizing,
                    [bodySizingBodyValues]
                  )
                )
              );
            }}
            icon={<Icon source={icons.IconSmallArrowRight} />}
            iconRight
          >
            {t("commons:filterModalPartialProductFinder.sizing.setFilterCtaButton")}
          </Button>
        )}
        <Button
          block
          variant={showSetFilterButton ? "standout" : "accent"}
          kind="solid"
          onClick={() => {
            onClose();
            dispatch(actions.modal.showModal("bodySizing", { variant: "productFinder" }));
          }}
          icon={<Icon source={icons.IconSmallArrowRight} />}
          iconRight
        >
          {t("commons:filterModalPartialProductFinder.sizing.resultsCtaButton")}
        </Button>
      </>
    );
  };

  const renderNoBodySizingValues = () => {
    return (
      <>
        <Headline kind="xs">{t("commons:filterModalPartialProductFinder.sizing.noResultsHeadline")}</Headline>
        <Button
          block
          variant={showSetFilterButton ? "standout" : "accent"}
          kind="solid"
          onClick={() => {
            onClose();
            dispatch(actions.modal.showModal("bodySizing", { variant: "productFinder" }));
          }}
          icon={<Icon source={icons.IconSmallArrowRight} />}
          iconRight
        >
          {t("commons:filterModalPartialProductFinder.sizing.noResultsCtaButton")}
        </Button>
      </>
    );
  };

  const renderContent = () => {
    if (!settings.isBodySizingEnabled) {
      if (settings.isBodySizingNotificationsEnabled) {
        return renderBodySizingNotification();
      } else {
        return null;
      }
    } else {
      if (showSizingWarning) {
        return renderWarning();
      } else if (!bodySizingBodyValues) {
        return renderNoBodySizingValues();
      } else {
        return renderBodySizingValues(bodySizingBodyValues);
      }
    }
  };

  return renderContent();
};

export default BodySizingFilterModalPartial;
